import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/hooks/useAuth';
import type { AxiosError } from 'axios';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AuthLayout } from './AuthLayout';
import { FormInput } from './FormInput';

/**
 * LoginPage component that matches the Atlas Restoration Figma design
 * Features:
 * - Email/Username and password inputs with icons
 * - Show/hide password toggle
 * - Form validation
 * - Loading states
 * - Error handling
 * - Atlas brand colors (blue #0a0a9e, red #d71f20)
 */
export function LoginPage() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Form state
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!usernameOrEmail.trim()) {
      setError('Email or username is required');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    setIsSubmitting(true);

    try {
      // API expects UsernameOrEmail and Password with capital letters
      await login({
        UsernameOrEmail: usernameOrEmail,
        Password: password,
      });

      // Navigate to dashboard on success
      navigate('/');
    } catch (err) {
      // Handle different error types
      if (err instanceof Error) {
        setError(err.message);
      } else if ((err as AxiosError).response) {
        const axiosError = err as AxiosError<{ message?: string; errors?: string[] }>;
        const errorMessage =
          axiosError.response?.data?.errors?.join(', ') ||
          axiosError.response?.data?.message ||
          'Login failed. Please check your credentials.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Welcome Back!" subtitle="Sign in to get started">
      <form onSubmit={handleSubmit} className="space-y-5">
        <ErrorAlert error={error} className="rounded-lg" />

        {/* Email/Username input */}
        <FormInput
          id="usernameOrEmail"
          label="Email or Username"
          type="text"
          placeholder="Enter your email or username"
          value={usernameOrEmail}
          onChange={(e) => setUsernameOrEmail(e.target.value)}
          icon={<Mail className="h-[21px] w-[26px]" />}
          disabled={isSubmitting}
          required
        />

        {/* Password input */}
        <FormInput
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          icon={<Lock className="h-[21px] w-[24px]" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-[21px] w-[25px]" />
              ) : (
                <Eye className="h-[21px] w-[25px]" />
              )}
            </button>
          }
          disabled={isSubmitting}
          required
        />

        {/* Forgot password link */}
        <div className="flex justify-end pr-2">
          <button
            type="button"
            className="text-[16px] font-semibold underline hover:opacity-80 text-secondary"
            onClick={() => {
              // TODO: Implement forgot password functionality
              console.log('Forgot password clicked');
            }}
          >
            Forgot password?
          </button>
        </div>

        {/* Login button */}
        <Button
          type="submit"
          className="h-[49px] w-full rounded-[20px] text-[24px] font-semibold text-white hover:opacity-90 bg-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Logging in...' : 'Log In'}
        </Button>

        {/* Sign up link */}
        <div className="text-center text-[16px]">
          <span className="font-medium text-black">Don't have an account? </span>
          <Link
            to="/register"
            className="font-semibold underline hover:opacity-80 text-secondary"
          >
            Sign up here
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
